<template>
	<div class="modal-container modal-x-large modal-document-preview">
		<div class="modal-header">
			<span>{{ title }}</span>

			<div class="buttons-container">
				<template v-if="isImpactDoc">
					<DropdownButton
						class="button button-print"
						label="Print Document"
						:disabled="isBusy || isLoadingPDF"
						:actions="impactPrintActions"
					/>

					<DropdownButton
						class="button button-download"
						label="Download Document"
						:disabled="isBusy || isLoadingPDF"
						:actions="impactDownloadActions"
					/>
				</template>
				<template v-else>
					<ButtonLoading
						class="button"
						:isLoading="isBusy"
						:disabled="isLoadingPDF"
						iconOnly
						@click="() => printPDF()"
					>
						Print
					</ButtonLoading>

					<ButtonLoading
						class="button"
						:isLoading="isBusy"
						:disabled="isLoadingPDF"
						iconOnly
						@click="() => downloadDocuments()"
					>
						Download
					</ButtonLoading>
				</template>

				<ButtonLoading
					v-if="fimenu.buyersOrderEnabled && displayCreditApplication"
					class="button button-load"
					:isLoading="isBusy"
					:disabled="isLoadingPDF || fimenu.isSpectator"
					iconOnly
					@click="sendToLender"
				>
					Send to RouteOne
				</ButtonLoading>

				<template v-if="canUploadSignedCopy && !hasDocBeenESigned">
					<ButtonLoading
						v-if="!isDocSigned"
						:isLoading="isBusy"
						loadingText="Loading data..."
						:class="['button', isDocSigned ? 'button-info' : 'button-save']"
						:disabled="!mergedPDF || isLoadingPDF || fimenu.isSpectator"
						@click="openUploadAndComparePdfModal"
					>
						{{ isDocSigned ? 'Replace Signed Copy' : 'Upload Signed Copy' }}
					</ButtonLoading>

					<DropdownButton
						v-else
						:label="isBusy ? 'Loading data...' : 'Manage Signed Copy'"
						:disabled="!mergedPDF || isLoadingPDF || isBusy || fimenu.isSpectator"
						:actions="manageWetSignedActions"
						class="button-manage-wet-signed"
					/>
				</template>
			</div>
		</div>

		<div class="modal-body">
			<div :style="$grid('1')">
				<IsBusySectionComponent v-if="isLoadingPDF" />

				<PDFViewer
					v-if="mergedPDF"
					ref="pdfViewer"
					:src="mergedPDF"
					:class="{ 'hide-component': isLoadingPDF }"
					style="overflow-x: none"
					@rendered="onPageRendered"
				/>
			</div>
		</div>

		<div class="modal-footer">
			<button
				class="button-unwind"
				@click="cancel()"
			>
				Close
			</button>
		</div>
	</div>
</template>
<script lang="ts">
	import { defineComponent, PropType } from 'vue';
	import { PDFDocument } from 'pdf-lib';

	import $modal from '@core/services/modal';
	import api from '@core/services/api/_api';
	import auth from '@core/services/auth';
	import util from '@core/services/util';

	import { DisplayCreditApplicationActions } from '@core/helpers/finance-channels-helper';
	import USBHelper from '@core/helpers/usb-helper';

	import ENUMS from '@core/classes/Enums';
	import FIMenu from '@core/classes/FIMenu';
	import { IPaperworkDocument } from '@core/classes/Paperwork/PaperworkDocument';
	import { OverlayTypes } from '@core/classes/SharedEnums';

	import ButtonLoading from '@core/components/ButtonLoading.vue';
	import DropdownButton from '@core/components/DropdownButton.vue';
	import IsBusySectionComponent from '@core/components/IsBusySectionComponent.vue';
	import modalInfo from '@core/modals/modalInfo.vue';
	import modalUploadAndComparePDF from '@core/modals/modalUploadAndComparePDF.vue';
	import PDFViewer from '@core/components/PDFViewer.vue';

	export interface IModalPreviewPDFProps {
		passedData: {
			/** Title of the modal */
			title?: string;

			/** Dictionary mapping Azure filepaths to base-64 byte strings */
			pdf?: Record<string, string>;
			fimenu?: FIMenu;

			packetSelectedDocuments?: string[];
			uploadedSelectedDocuments?: string[];

			paperworkDocument?: IPaperworkDocument;
			canUploadSignedCopy?: boolean;

			loadPDFs?: (packetSelectedDocuments: string[], uploadedSelectedDocuments: string[]) => Promise<Record<string, string>>;
			uploadSignedDoc?: (documentId: string, fileData: File) => Promise<IPaperworkDocument>;
			removeSignedDoc?: (documentId: string) => Promise<IPaperworkDocument>;
		};
	}

	export default defineComponent({
		name: 'modalPreviewPDF',
		components: {
			PDFViewer,
			IsBusySectionComponent,
			ButtonLoading,
			DropdownButton,
		},
		props: {
			modal: Object as PropType<IModalPreviewPDFProps>,
		},
		data() {
			return {
				title: 'Document Preview',
				pdf: {} as Record<string, string>, // This is a dictionary mapping Azure filepaths to base-64 byte strings
				fimenu: null as FIMenu,
				packetSelectedDocuments: [] as string[],
				uploadedSelectedDocuments: [] as string[],
				paperworkDocument: null as IPaperworkDocument,
				canUploadSignedCopy: false,

				isBusy: false,
				isLoadingPDF: false,
				mergedPDF: null as string, // Base-64 byte string of all provided PDFs
				renderedPDFCount: 0,
				signedDocUploaded: false,
				filledDocumentData: null as string,
			};
		},
		computed: {
			user() {
				return auth.getTokenPayload();
			},
			pdfEntries(): string[] {
				if (!this.pdf) return [];
				return Object.entries(this.pdf).map(x => x[0]);
			},
			arrayOfPDFs(): string[] {
				if (!this.pdf) return [];
				return Object.values(this.pdf).map(pdfObj => pdfObj);
			},
			displayCreditApplication(): boolean {
				return DisplayCreditApplicationActions(this.fimenu as FIMenu) && !!this.fimenu?.dealJacket?.applications.some(app => app.selected);
			},
			documentId(): string {
				return this.paperworkDocument?.id;
			},
			isImpactDoc(): boolean {
				return !!this.paperworkDocument?.impactDoc;
			},
			isDocSigned(): boolean {
				return this.paperworkDocument?.status === ENUMS.PaperworkDocumentStatus.Signed || this.signedDocUploaded;
			},
			hasDocBeenESigned(): boolean {
				if (!this.documentId || this.paperworkDocument.overlays.length < 1) return false;

				if (this.paperworkDocument.isWetSigned) return false;

				return this.paperworkDocument.overlays
					.filter(o => o.overlayType === OverlayTypes.Signature || o.overlayType === OverlayTypes.Initials)
					.every(o => o.signature?.isSigned || o.initials?.isSigned);
			},
			impactPrintActions(): any[] {
				if (!this.isImpactDoc) return [];

				const actions: any[] = [
					{
						label: 'Blank to Impact Printer',
						class: 'button',
						svg: '/static/img/fluxCapacitor.svg#fluxCapacitor',
						click: async () => await USBHelper.printToImpactPrinter(this.paperworkDocument.impactDoc),
					},
					{
						label: 'Blank to Laser Printer',
						class: 'button',
						icon: 'fas fa-print',
						click: async () => await this.printOneDocument(this.paperworkDocument.impactDocPdf, 'application/pdf'),
					},
				];

				if (this.isDocSigned) {
					actions.push({
						label: 'Signed to Laser Printer',
						class: 'button',
						icon: 'fas fa-signature',
						click: () => this.printPDF(),
					});
				}

				return actions;
			},
			impactDownloadActions(): any[] {
				if (!this.isImpactDoc) return [];

				const actions: any[] = [
					{
						label: 'Blank PDF',
						class: 'button',
						click: async () => await this.downloadOneDocument(this.paperworkDocument.impactDocPdf, 'pdf'),
					},
					{
						label: 'Blank PRN',
						class: 'button',
						click: async () => await this.downloadOneDocument(this.paperworkDocument.impactDoc, 'prn'),
					},
				];

				if (this.isDocSigned) {
					actions.push({
						label: 'Signed',
						class: 'button',
						click: () => this.downloadDocuments(),
					});
				}

				return actions;
			},
			manageWetSignedActions(): any[] {
				if (!this.canUploadSignedCopy || this.hasDocBeenESigned) return [];

				const actions: any[] = [
					{
						label: this.isDocSigned ? 'Replace Signed Copy' : 'Upload Signed Copy',
						class: this.isDocSigned ? 'button-accept' : 'button-save',
						click: async () => await this.openUploadAndComparePdfModal(),
					},
				];

				if (this.isDocSigned) {
					actions.push({
						label: 'Delete Signed Copy',
						class: 'button-unwind',
						click: () => this.openDeleteWetSignedModal(),
					});
				}

				return actions;
			},
		},
		created() {
			this.title = this.modal.passedData.title ?? 'Document Preview';
			this.pdf = this.modal.passedData.pdf;
			this.fimenu = this.modal.passedData.fimenu;
			this.packetSelectedDocuments = this.modal.passedData.packetSelectedDocuments ?? [];
			this.uploadedSelectedDocuments = this.modal.passedData.uploadedSelectedDocuments ?? [];
			this.paperworkDocument = this.modal.passedData.paperworkDocument;
			this.canUploadSignedCopy = this.modal.passedData.canUploadSignedCopy;

			if (this.paperworkDocument) {
				this.packetSelectedDocuments.push(this.paperworkDocument.signedDoc ?? this.paperworkDocument.filledDoc);
			}

			this.loadPDF();
		},
		methods: {
			cancel() {
				$modal.cancel();
			},
			async mergePDF(arrayOfPDF: string[]) {
				// arrayOfPDF: Array of PDF in base64.

				this.isBusy = true;
				try {
					const mergedPdf = await PDFDocument.create();

					for (const base64Pdf of arrayOfPDF) {
						const pdfBuffer = util.base64ToUint8Array(base64Pdf);
						const pdf = await PDFDocument.load(pdfBuffer);
						const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
						pages.forEach(page => mergedPdf.addPage(page));
					}

					// Serialize the PDFDocument to bytes (a Uint8Array)
					const mergedPdfBytes = await mergedPdf.save();

					// Set merge pdf
					this.mergedPDF = util.uint8ArrayToBase64(mergedPdfBytes);
				} catch (err) {
					console.error('Error Merging PDF: ', err);
				} finally {
					this.isBusy = false;
				}
			},
			async loadPDF(): Promise<void> {
				this.isLoadingPDF = true; // Will be set to false when the pageRendered() callback is triggered

				try {
					if (this.paperworkDocument) {
						this.pdf = await this.modal.passedData.loadPDFs([this.paperworkDocument.signedDoc ?? this.paperworkDocument.filledDoc], []);
						if (!this.pdf)
							throw new Error('Unable to load PDF from server.', {
								cause: this.pdf,
							});

						// We only loaded in one document. No need to merge.
						this.mergedPDF = Object.entries(this.pdf)[0][1];

						// If we loaded in the filled doc
						if (!this.paperworkDocument.signedDoc) this.filledDocumentData = this.mergedPDF;
					} else {
						this.pdf = await this.modal.passedData.loadPDFs(this.packetSelectedDocuments, this.uploadedSelectedDocuments);
						await this.mergePDF(this.arrayOfPDFs);
					}
				} catch (err) {
					console.error('Error Loading PDF: ', err);
					util.toastr('error', 'Error Loading PDF', 'A server error occurred when trying to load the PDF.');
					this.isLoadingPDF = false;
				}
			},
			async loadFilledPaperwork(): Promise<void> {
				this.isBusy = true;
				try {
					if (!this.paperworkDocument.filledDoc) throw new Error('The filled paperwork document is not available. Cannot load document.');

					const filledPdfData = await this.modal.passedData.loadPDFs([this.paperworkDocument.filledDoc], []);
					this.filledDocumentData = Object.entries(filledPdfData)[0][1];
				} catch (err) {
					console.error('Error loading in filled PDF document: ', err);
					util.toastr('error', 'Error Loading Document', 'Was unable to load the original copy of the document.');
				} finally {
					this.isBusy = false;
				}
			},
			async downloadOneDocument(filepath: string, fileExtension: string = 'pdf'): Promise<void> {
				this.isBusy = true;
				try {
					const docName = this.getDocumentName(filepath) ?? 'Packet';
					await api.fimenu.downloadDocument(filepath, `${docName} - ${this.fimenu.dealNumber} - ${this.fimenu.storeCode}.${fileExtension}`);
					const sessionInfo = await api.fimenu.getSessionInfo();
					this.fimenu.addToLog(
						this.fimenu.paperwork.currentPacket().packetDateStamp,
						'Download',
						docName,
						this.user.EmployeeName,
						JSON.stringify(sessionInfo.data),
					);
				} catch (err) {
					console.error('Error when downloading document: ', err);
					util.toastr('error', 'Error Download Document', 'An error occurred when trying to download the document.');
				} finally {
					this.isBusy = false;
				}
			},
			async printOneDocument(filepath: string, mimeFileType: string) {
				this.isBusy = true;

				try {
					const docBytes = await this.getDocumentBytes(filepath);

					const docBlob = util.base64ToBlob(docBytes, mimeFileType);
					util.printBlob(docBlob);
				} catch (err) {
					console.error('Error when printing document: ', err);
					util.toastr('error', 'Error Printing Document', 'An error occurred when trying to print the document.');
				} finally {
					this.isBusy = false;
				}
			},
			async getDocumentBytes(filepath: string): Promise<any> {
				this.isBusy = true;
				try {
					const response = await api.fimenu.getDocumentBytes(filepath);
					return response?.data;
				} finally {
					this.isBusy = false;
				}
			},
			async downloadDocuments(): Promise<void> {
				this.isBusy = true;

				try {
					const pdfBlobFile = await util.base64ToBlob(this.mergedPDF, 'application/pdf');
					let docName = null;
					if (this.pdfEntries.length === 1) docName = this.getDocumentName(this.pdfEntries[0]);

					docName = docName ?? 'Packet';

					const fileName = `${docName} - ${this.fimenu.dealNumber} - ${this.fimenu.storeCode}`;
					await util.downloadBlob(pdfBlobFile, `${fileName}.pdf`);
					const sessionInfo = await api.fimenu.getSessionInfo();
					this.fimenu.addToLog(
						this.fimenu.paperwork?.currentPacket().packetDateStamp,
						'Download',
						docName,
						this.user.EmployeeName,
						JSON.stringify(sessionInfo.data),
					);
				} catch (error) {
					console.error('Error downloading PDF:', error);
					util.toastr('error', 'Error', 'Could not download the file.');

					// Propagate the error to the caller
					throw error;
				} finally {
					this.isBusy = false;
				}
			},
			getDocumentName(filepath: string): string {
				const currentDocuments = this.fimenu.paperwork?.currentPacket()?.documents;

				let docDescription =
					currentDocuments?.find((document: any) => {
						return document.signedDoc === filepath || document.filledDoc === filepath || document.impactDoc === filepath;
					})?.description ?? null;

				if (!docDescription) docDescription = this.fimenu.paperwork.uploadedDocuments.find((d: any) => d.id === filepath)?.docInfo?.docType ?? null;

				return docDescription;
			},
			async printPDF(): Promise<void> {
				this.isBusy = true;

				try {
					const pdfBlobFile = await util.base64ToBlob(this.mergedPDF, 'application/pdf');
					util.printBlob(pdfBlobFile);
				} catch (err) {
					console.error('Error Printing PDF: ', err);
					util.toastr('error', 'Error', 'Could not print the file.');
				} finally {
					this.isBusy = false;
				}
			},
			async sendToLender(): Promise<void> {
				this.isBusy = true;

				try {
					const response = await api.financeChannels.submitDocuments(this.fimenu.id, this.packetSelectedDocuments, this.uploadedSelectedDocuments);

					if (response.data.statusCode == 200) {
						// Successfully sent
						util.toastr('success', 'Success', 'Document(s) successfully submitted to RouteOne.');
					} else {
						// Handled error
						const messages = response.data.messages;

						if (messages && messages.length > 0) {
							const joinedMessages = messages.join(' ');
							util.toastr('error', 'Error', joinedMessages);
						} else {
							util.toastr('error', 'Error', `Unexpected sever error. Files could not be sent. Error #${response?.data?.status ?? 500}`);
						}
					}
				} catch (err: any) {
					const errorMessage = err?.message ? err.message : 'An unexpected error occurred.';
					util.toastr('error', 'Error', errorMessage);
				} finally {
					this.isBusy = false;
				}
			},
			onPageRendered() {
				if (this.isLoadingPDF) this.isLoadingPDF = false;
			},
			async openUploadAndComparePdfModal() {
				if (!this.modal.passedData.uploadSignedDoc) throw new Error('Cannot upload signed doc. Missing upload method.');

				if (this.filledDocumentData == null) await this.loadFilledPaperwork();

				$modal.open(modalUploadAndComparePDF, {
					name: 'modalUploadAndComparePDF',
					passedData: {
						title: 'Upload Signed Document',
						originalPDF: this.filledDocumentData,
						uploadedDocPrefilled: this.paperworkDocument.signedDoc ? this.mergedPDF : null,
						saveFunc: async (fileData: File) => {
							const updatedPaperwork = await this.modal.passedData.uploadSignedDoc(this.documentId, fileData);

							if (updatedPaperwork) this.paperworkDocument = updatedPaperwork;
						},
					},
					postFunction: async (data: { base64: string }) => {
						if (data?.base64) {
							this.isLoadingPDF = true;
							this.mergedPDF = null; // Forces PDFViewer to unmount before updating PDF data.
							await this.mergePDF([data.base64]);
							this.signedDocUploaded = true;
						}
					},
				});
			},
			openDeleteWetSignedModal() {
				$modal.open(modalInfo, {
					passedData: {
						title: 'Delete Signed PDF',
						info: 'Are you sure you want to delete the wet-signed copy of this document? This action cannot be undone.',
						acceptText: 'Delete',
						acceptTextClass: 'button-unwind',
						cancelText: 'Cancel',
						cancelTextClass: 'button',
					},
					postFunction: async () => {
						const updatedPaperwork = await this.modal.passedData.removeSignedDoc(this.documentId);

						if (updatedPaperwork) {
							this.paperworkDocument = updatedPaperwork;
							this.mergedPDF = null;
							await this.loadPDF();
						}
					},
				});
			},
		},
	});
</script>
<style>
	.modal-document-preview {
		height: 100%;
	}

	.modal-document-preview .buttons-container {
		display: flex;
		flex-direction: row;
		gap: 10px;
	}

	.modal-document-preview .buttons-container > button {
		width: fit-content;
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
		gap: 10px;
	}

	.modal-document-preview .button-dropdown-container.button-print > .button-dropdown > button,
	.modal-document-preview .button-dropdown-container.button-download > .button-dropdown > button {
		background-color: var(--main-color);
		border-color: var(--main-color);
	}

	.modal-document-preview .button-dropdown-container.button-manage-wet-signed > .button-dropdown > button {
		background-color: var(--print-color);
		border-color: var(--print-color);
	}

	.modal-document-preview .hide-component {
		opacity: 0;
	}

	.modal-document-preview > .modal-body > .IsBusySectionComponent-container {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}
</style>
